import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InvoiceForm } from "../../components/Invoice/InvoiceForm";
import { InvoiceStatusEnum } from "../../types/invoice.types";
import { CreateInvoiceDto } from "../../types/invoice.types";
import { createInvoice } from "../../service/invoice/InvoiceServices";
import { getClientById } from "../../service/client/ClientServices";
import { createItem } from "../../service/item/ItemServices";
import { CreateItemDto } from "@/types/item.types";
import { showToast } from "../../store/slices/toastSlice";

export const CreateInvoice: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [preselectedClient, setPreselectedClient] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);

  // Extract client ID from query params if available
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const clientId = queryParams.get("clientId");

    if (clientId) {
      fetchClientData(clientId);
    }
  }, [location]);

  // Fetch client data if clientId is provided
  const fetchClientData = async (clientId: string) => {
    try {
      const response = await getClientById(dispatch, clientId);

      if (response?.success) {
        setPreselectedClient({
          id: response.data.client_id,
          name: response.data.client_name,
          email: response.data.email,
          address: {
            line1: response.data.address_line1,
            line2: response.data.address_line2,
            city: response.data.city,
            stateProvince: response.data.state_province,
            postalCode: response.data.postal_code,
            country: response.data.country_name,
          },
          taxNumber: response.data.tax_number,
          taxExempt: response.data.tax_exempt,
          industry: response.data.industry,
        });
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
    }
  };

  const formatInvoiceData = (data: CreateInvoiceDto): CreateInvoiceDto => {
    const invoiceItems =
      data.invoice_items?.map((item) => ({
        item_id: item.item_id ? parseInt(item.item_id.toString()) : undefined,
        item_code: item.item_code || "",
        item_name: item.item_name || "",
        sacCode: item.sacCode || "",
        sac_gst_rate: item.sac_gst_rate || 0,
        taxName: item.taxName || "",
        description: item.description || "",
        quantity: item.quantity || 0,
        unit_price:
          typeof item.unit_price === "string"
            ? parseFloat(item.unit_price)
            : item.unit_price,
        unit_type: item.unit_type || "",
        tax_rate_id: item.tax_rate_id || undefined,
        sort_order: item.sort_order || 0,
        discount: item.discount || 0,
        documents: data.documents || [],
      })) || [];

    return {
      client_id: parseInt(data.client_id?.toString() || "0"),
      clientName: data?.clientName,
      currency_code: data?.currency_code || "USD",
      issue_date: data.issue_date || new Date(),
      due_date: data.due_date || new Date(),
      currency_id: data.currency_id || 1,
      industry_id: data.industry_id,
      tax_inclusive: data.tax_inclusive || false,
      payment_terms: data.payment_terms,
      payment_term_id: data.payment_term_id,
      status: data?.status,
      business_type: data.business_type,
      notes: data.notes,
      invoiceNumber: data.invoiceNumber,
      terms_conditions: data.terms_conditions,
      template_id: data.template_id,
      is_recurring: data.is_recurring || false,
      parent_invoice_id: data.parent_invoice_id,
      recurrence_pattern: data.recurrence_pattern,
      next_recurrence_date: data.next_recurrence_date,
      business_type_id: data.business_type_id,
      invoice_items: invoiceItems,
      emailTo: data.emailTo,
      emailCc: data.emailCc,
      emailMessage: data?.emailMessage,
      emailSubject: data.emailSubject,
      natureOfPayment: data.natureOfPayment || null,
      threashold: data.threashold || null,
      gstType: data.gstType || null,
      gstRate: data.gstRate || 0,
      yearOfGst: data.yearOfGst || null,
      documents: data?.documents,
      withPaymentLink: data.withPaymentLink,
      round_off: data.round_off,
      choosedTemplateId: data.choosedTemplateId,
    };
  };

  const handleSubmit = async (data: CreateInvoiceDto, skipNavigate: boolean = false) => {
    // Validate required fields
    if (!data.client_id) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Client is required",
        life: 3000,
      });
      return;
    }

    if (!data.invoice_items || data.invoice_items.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "At least one item is required",
        life: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Format data for API
      const invoiceData: any = formatInvoiceData(data);
      const missingItems: CreateItemDto[] = data.invoice_items
        .filter((item: any) => !item?.item_id) // Filter items where item_id does not exist
        .map((item: any) => ({
          item_code: null,
          item_name: item.item_name || "",
          sacCode: item.sacCode || null,
          sac_gst_rate: item.sac_gst_rate || 0,
          description: item.description || "",
          unit_price: item.unit_price || 0,
          unit_type: item.unit_type || "",
          tax_rate_id: item.tax_rate_id || undefined,
          item_type: "",
        }));
      const item_names = missingItems.map((i) => i.item_name.trim());
      var isDuplicate = item_names.some(function (item, idx) {
        return item_names.indexOf(item) != idx;
      });

      if (isDuplicate) {
        dispatch(
          showToast({
            severity: "error",
            summary: "Validation Error",
            detail: "Item name can not be same!",
          })
        );
        return;
      }

      const createdItems = await createItem(dispatch, missingItems);
      if (Array.isArray(createdItems?.result)) {
        // Update invoice_items with newly created item_ids
        invoiceData.invoice_items = data.invoice_items.map((item: any) => {
          if (!item?.item_id) {
            const matched = createdItems.result.find(
              (created: any) => created.item_name === item.item_name
            );
            if (matched) {
              const { isNew, ...otherItemProperties } = item;
              return {
                ...otherItemProperties,
                item_id: matched.item_id,
                item_code: matched.item_code,
              };
            }
          }
          const { isNew, ...otherItemProperties } = item;
          return otherItemProperties;
        });
      }
      // Call the API
      const response = await createInvoice(dispatch, invoiceData);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Invoice created successfully",
          life: 3000,
        });

        //send the invoice
        if (invoiceData.status == InvoiceStatusEnum.PENDING) {
        }

        // After successful creation, redirect to invoice list after a short delay
        setTimeout(() => {
          if (!skipNavigate) {
            navigate("/invoices");
          }

        }, 1500);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: response?.message || "Failed to create invoice",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error creating invoice:", error);

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to create invoice. Please try again.",
        life: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Toast ref={toast} />

      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold surface-900 mb-4">
          Create New Invoice
        </h1>

        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-outlined p-2 pe-3"
          onClick={() => navigate("/invoices")}
        />
      </div>

      <InvoiceForm
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        initialData={{
          status: InvoiceStatusEnum.DRAFT,
        }}
        mode="create"
      />
    </div>
  );
};
